import { Fragment, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { useTRPC } from "../lib/trpc.js";
import { Card, Empty, ErrorBox, PageHeader, Spinner } from "../components/ui.js";
import { COMPETITION_TAGS, formatDate, tagLabel } from "../lib/format.js";
import { useSession } from "../lib/session.js";

const PAGE_SIZE = 12;

export default function News() {
  const trpc = useTRPC();
  const { isCommissioner } = useSession();
  const [page, setPage] = useState(0);
  // The filter lives in the URL, so "every preview" is a link someone can
  // share, and an article's tag chips can land on it.
  const [params, setParams] = useSearchParams();
  const tag = params.get("tag");
  const setTag = (t: string | null) => {
    const next = new URLSearchParams(params);
    if (t) next.set("tag", t);
    else next.delete("tag");
    setParams(next, { replace: true });
  };

  const query = useQuery(
    trpc.articles.list.queryOptions({
      limit: PAGE_SIZE,
      offset: page * PAGE_SIZE,
      tag,
      includeDrafts: isCommissioner,
    }),
  );
  const tags = useQuery(trpc.articles.tags.queryOptions());
  // Competitions first, in season order; then the categories, biggest first
  // (the server already sorts by count).
  const tagList = [
    ...(tags.data ?? [])
      .filter((t) => COMPETITION_TAGS.has(t.tag))
      // Seasons in order, then "playoffs", which cuts across them.
      .sort((a, b) => Number(a.tag === "playoffs") - Number(b.tag === "playoffs") || a.tag.localeCompare(b.tag)),
    ...(tags.data ?? []).filter((t) => !COMPETITION_TAGS.has(t.tag)),
  ];

  const total = query.data?.total ?? 0;
  const pages = Math.ceil(total / PAGE_SIZE);

  return (
    <div>
      <PageHeader
        title="News"
        subtitle={`${total} article${total === 1 ? "" : "s"} in the archive`}
        action={
          isCommissioner && (
            <Link to="/admin/articles/new" className="btn-primary">
              New article
            </Link>
          )
        }
      />

      {(tags.data?.length ?? 0) > 0 && (
        <div className="mb-6 flex flex-wrap gap-2">
          <button
            onClick={() => {
              setTag(null);
              setPage(0);
            }}
            className={`chip ${tag == null ? "border-blitz-500 text-blitz-400" : ""}`}
          >
            All
          </button>
          {tagList.map((t, i) => (
            <Fragment key={t.tag}>
              {/* A gap where the competitions end and the categories begin. */}
              {i > 0 && COMPETITION_TAGS.has(tagList[i - 1].tag) && !COMPETITION_TAGS.has(t.tag) && (
                <span className="mx-1 self-center text-chalk-600" aria-hidden>
                  ·
                </span>
              )}
            <button
              onClick={() => {
                setTag(t.tag);
                setPage(0);
              }}
              className={`chip ${tag === t.tag ? "border-blitz-500 text-blitz-400" : ""}`}
            >
              {tagLabel(t.tag)}
              <span className="text-chalk-500">{t.count}</span>
            </button>
            </Fragment>
          ))}
        </div>
      )}

      {query.isLoading && <Spinner />}
      {query.error && <ErrorBox error={query.error} />}

      {query.data?.articles.length === 0 && (
        <Card>
          <Empty>No articles yet.</Empty>
        </Card>
      )}

      <div className="grid gap-4 md:grid-cols-2">
        {query.data?.articles.map((a) => (
          <Link key={a.id} to={`/news/${a.slug}`} className="block">
            <article className="card h-full p-5 transition hover:border-field-600">
              <div className="flex items-center gap-2 text-[11px] uppercase tracking-wider text-chalk-500">
                {/* Archive pieces carry no date — none was ever recorded. */}
                {a.publishedAt && <time>{formatDate(a.publishedAt)}</time>}
                {a.seasonName && (
                  <span>{a.publishedAt ? "· " : ""}{a.seasonName}</span>
                )}
                {a.status === "draft" && (
                  <span className="chip border-flag-500/40 text-flag-400">
                    Draft
                  </span>
                )}
              </div>
              <h2 className="mt-2 text-lg font-bold leading-snug">{a.title}</h2>
              {a.subtitle && (
                <p className="mt-1 text-sm italic text-chalk-400">{a.subtitle}</p>
              )}
              <p className="mt-2 line-clamp-3 text-sm text-chalk-400">
                {a.excerpt}
              </p>
            </article>
          </Link>
        ))}
      </div>

      {pages > 1 && (
        <nav className="mt-8 flex items-center justify-center gap-2">
          <button
            className="btn-secondary"
            disabled={page === 0}
            onClick={() => setPage((p) => p - 1)}
          >
            Previous
          </button>
          <span className="px-3 text-sm text-chalk-400">
            Page {page + 1} of {pages}
          </span>
          <button
            className="btn-secondary"
            disabled={page + 1 >= pages}
            onClick={() => setPage((p) => p + 1)}
          >
            Next
          </button>
        </nav>
      )}
    </div>
  );
}
